import { useEffect, useRef } from 'react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * MatchUnlockedCelebration — silent background component that fires the
 * "MATCH UNLOCKED · +$10" moment as soon as the backend's
 * try_grant_first_load_match() drops the toast_pending flag on the user.
 *
 * Mounted app-wide (App.js) so the celebration fires no matter which page
 * the player is on when their cashier posts the load. Polls the same
 * rewards status endpoint MatchReadyChip already polls — cheap.
 *
 * Once fired, POSTs /rewards/ack-first-load-match to clear the flag so
 * refreshes don't re-trigger it. Uses a session-scoped useRef guard to
 * avoid double-firing while the ack request is in flight.
 */
const MatchUnlockedCelebration = () => {
    const { user, refreshUser } = useAuth();
    const firedRef = useRef(false);
    const pollRef = useRef(null);

    useEffect(() => {
        if (!user || user.role !== 'user') return;
        let cancelled = false;

        const checkPending = async () => {
            if (firedRef.current) return;
            try {
                const { data } = await axios.get(`${API}/rewards/onboarding-status`);
                if (cancelled) return;
                if (data?.first_load_match_toast_pending) {
                    firedRef.current = true;
                    const amount = data.first_load_match_amount || 10;
                    fireCelebration(amount);
                    // Fire-and-forget ack — if it fails we'll refire next
                    // session which is annoying but not damaging.
                    try {
                        await axios.post(`${API}/rewards/ack-first-load-match`);
                    } catch { /* silent */ }
                    // Refresh wallet chip so the bonus_credits jump is visible.
                    try { refreshUser?.(); } catch { /* ignore */ }
                }
            } catch { /* silent */ }
        };

        // Immediate check on mount, then poll every 25s while the player
        // is unclaimed. Cheap — same endpoint MatchReadyChip already hits.
        checkPending();
        pollRef.current = setInterval(checkPending, 25_000);
        return () => {
            cancelled = true;
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [user, refreshUser]);

    return null;
};

// ── Celebration effect ─────────────────────────────────────────────────
const fireCelebration = (amount) => {
    // Golden coin-burst — three staggered bursts for a satisfying pop.
    const goldPalette = ['#FFD700', '#FFE55C', '#39FF14', '#FFFFFF', '#B8860B'];
    const shoot = (opts) => {
        try {
            confetti({
                particleCount: 60,
                spread: 70,
                startVelocity: 45,
                gravity: 0.9,
                scalar: 1.1,
                ticks: 200,
                colors: goldPalette,
                zIndex: 9999,
                ...opts,
            });
        } catch { /* confetti library may be missing */ }
    };
    shoot({ origin: { x: 0.25, y: 0.65 }, angle: 60 });
    setTimeout(() => shoot({ origin: { x: 0.75, y: 0.65 }, angle: 120 }), 200);
    setTimeout(() => shoot({
        origin: { x: 0.5, y: 0.35 },
        particleCount: 90,
        spread: 100,
        startVelocity: 55,
    }), 380);

    toast(`MATCH UNLOCKED · +$${amount}`, {
        icon: '🎉',
        duration: 8000,
        description: 'Your new-player match landed! Bonus credits are ready to play — 1× wager rule applies.',
        style: {
            background: 'linear-gradient(135deg, #10261a, #052500)',
            border: '2px solid #39FF14',
            color: '#39FF14',
            fontWeight: '800',
            boxShadow: '0 8px 32px rgba(57,255,20,0.4)',
        },
    });
};

export default MatchUnlockedCelebration;
